import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataGridPremium, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid-premium';
import { format, subDays } from 'date-fns';

import DateRangePicker from '../components/DateRangePicker';
import { getReports } from '../services/stats.service';
import type { DailyReport } from '../types';

function fmtNumber(n: number): string {
  return (n ?? 0).toLocaleString();
}

interface Totals {
  clicks: number;
  conversions: number;
  payout: number;
}

function computeTotals(rows: DailyReport[]): Totals {
  return rows.reduce(
    (acc, row) => ({
      clicks: acc.clicks + row.clicks,
      conversions: acc.conversions + row.conversions,
      payout: acc.payout + row.payout,
    }),
    { clicks: 0, conversions: 0, payout: 0 },
  );
}

function buildColumns(showCountry: boolean, showChannel: boolean): GridColDef[] {
  const cols: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      width: 130,
    },
  ];

  if (showCountry) {
    cols.push({
      field: 'country',
      headerName: 'Country',
      width: 100,
      valueFormatter: (value: string | null) => value || '-',
    });
  }

  if (showChannel) {
    cols.push({
      field: 'channel',
      headerName: 'Channel',
      width: 160,
      valueFormatter: (value: string | null) => value || '-',
    });
  }

  cols.push(
    {
      field: 'clicks',
      headerName: 'Clicks',
      width: 110,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => fmtNumber(value),
    },
    {
      field: 'conversions',
      headerName: 'Conversions',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => fmtNumber(value),
    },
    {
      field: 'payout',
      headerName: 'Payout',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => '$' + (value ?? 0).toFixed(2),
      cellClassName: 'payout-bold',
    },
    {
      field: 'cr',
      headerName: 'CR%',
      width: 100,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value: unknown, row: DailyReport) =>
        row.clicks > 0 ? (row.conversions / row.clicks) * 100 : 0,
      valueFormatter: (value: number) => (value ?? 0).toFixed(2) + '%',
    },
    {
      field: 'epc',
      headerName: 'EPC',
      width: 100,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value: unknown, row: DailyReport) =>
        row.clicks > 0 ? row.payout / row.clicks : 0,
      valueFormatter: (value: number) => '$' + (value ?? 0).toFixed(2),
    },
    {
      field: 'locked_at',
      headerName: 'Finalized',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
        ) : (
          <ScheduleIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        ),
    },
  );

  return cols;
}

function exportCSV(rows: DailyReport[], showCountry: boolean, showChannel: boolean) {
  const header = ['Date'];
  if (showCountry) header.push('Country');
  if (showChannel) header.push('Channel');
  header.push('Clicks', 'Conversions', 'Payout', 'CR%', 'EPC', 'Finalized');

  const csvRows = rows.map((r) => {
    const cr = r.clicks > 0 ? ((r.conversions / r.clicks) * 100).toFixed(2) : '0.00';
    const epc = r.clicks > 0 ? (r.payout / r.clicks).toFixed(4) : '0.0000';
    const cols: (string | number)[] = [r.date];
    if (showCountry) cols.push(r.country || '');
    if (showChannel) cols.push(r.channel || '');
    cols.push(r.clicks, r.conversions, r.payout.toFixed(2), cr, epc, r.locked_at ? 'Yes' : 'No');
    return cols.join(',');
  });

  const csv = [header.join(','), ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `daily-reports-${rows[0]?.date ?? 'export'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const ReportsPage: React.FC = () => {
  const [from, setFrom] = useState(() => format(subDays(new Date(), 29), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [byCountry, setByCountry] = useState(false);
  const [byChannel, setByChannel] = useState(false);
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(true);

  const handleDateChange = useCallback((newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
  }, []);

  const groupByParam = useMemo(() => {
    const parts: string[] = [];
    if (byCountry) parts.push('country');
    if (byChannel) parts.push('flow_id');
    return parts.length > 0 ? parts.join(',') : undefined;
  }, [byCountry, byChannel]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);

    getReports(from, to, groupByParam)
      .then((data) => {
        if (cancelled) return;
        setReports(data);
      })
      .catch((err) => {
        console.error('Failed to load reports:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [from, to, groupByParam]);

  const columns = useMemo(() => buildColumns(byCountry, byChannel), [byCountry, byChannel]);
  const totals = useMemo(() => computeTotals(reports), [reports]);
  const totalCr = totals.clicks > 0 ? ((totals.conversions / totals.clicks) * 100).toFixed(2) : '0.00';
  const totalEpc = totals.clicks > 0 ? (totals.payout / totals.clicks).toFixed(4) : '0.0000';

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h4">Daily Reports</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <DateRangePicker from={from} to={to} onChange={handleDateChange} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={() => exportCSV(reports, byCountry, byChannel)}
            disabled={reports.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Group By Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={byCountry} onChange={(e) => setByCountry(e.target.checked)} size="small" />}
          label="By Country"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
        <FormControlLabel
          control={<Checkbox checked={byChannel} onChange={(e) => setByChannel(e.target.checked)} size="small" />}
          label="By Channel"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
      </Box>

      {/* Table */}
      <Card sx={{ p: 3 }}>
        {loading ? (
          <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
        ) : (
          <>
            <DataGridPremium
              rows={reports}
              columns={columns}
              getRowId={(row: DailyReport, index?: number) =>
                `${row.date}_${row.country ?? ''}_${row.channel ?? ''}_${index ?? 0}`
              }
              density="compact"
              disableRowSelectionOnClick
              hideFooter={reports.length <= 100}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'date', sort: 'desc' }],
                },
              }}
              sx={{
                border: 'none',
                height: 'calc(100vh - 380px)',
                minHeight: 300,
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(108, 142, 255, 0.04)',
                },
                '& .payout-bold': {
                  fontWeight: 700,
                },
              }}
            />

            {/* Totals Row */}
            {reports.length > 0 && !byCountry && !byChannel && (
              <Box
                sx={{
                  display: 'flex',
                  px: 1,
                  py: 1.5,
                  borderTop: '2px solid rgba(255,255,255,0.12)',
                  mt: 0.5,
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                }}
              >
                <Box sx={{ width: 130 }}>Totals</Box>
                <Box sx={{ width: 110, textAlign: 'right' }}>{fmtNumber(totals.clicks)}</Box>
                <Box sx={{ width: 130, textAlign: 'right' }}>{fmtNumber(totals.conversions)}</Box>
                <Box sx={{ width: 130, textAlign: 'right' }}>${totals.payout.toFixed(2)}</Box>
                <Box sx={{ width: 100, textAlign: 'right' }}>{totalCr}%</Box>
                <Box sx={{ width: 100, textAlign: 'right' }}>${totalEpc}</Box>
                <Box sx={{ width: 100 }} />
              </Box>
            )}
          </>
        )}
      </Card>
    </Box>
  );
};

export default ReportsPage;
